/**
 * The chip that follows granny and says what she is doing.
 *
 * Plain DOM projected into screen space, NOT a mesh. Two reasons, both hard
 * constraints: there are fewer than six draw calls of headroom against the 120
 * budget (see AGENTS.md), and the HUD is already DOM over the canvas so this
 * costs nothing to style, scale or localise.
 *
 * It exists because reading granny's schedule is the skill the game teaches
 * (DESIGN.md §4) and, until it did, napping and watching TV looked identical.
 */

import type { ActivityPresentation, HearingBand } from '@/ai/routine';
import { tuning } from '@/core/tuning';

/** Icon-led, exactly as the HUD demands: the glyph carries it, words are backup. */
const EYE_ICON: Record<'open' | 'shut', string> = { open: '👁️', shut: '🙈' };

const EAR_ICON: Record<HearingBand, string> = {
  sharp: '📢',
  normal: '👂',
  dulled: '🔇',
  deaf: '🔇',
};

export interface ChipPlacement {
  /** Screen pixels from the top-left of the canvas. */
  x: number;
  y: number;
  visible: boolean;
}

/**
 * Where the chip goes, as arithmetic.
 *
 * Split out from the DOM so the off-screen and behind-camera cases are testable
 * without a browser. `ndc` is the projected head position in normalised device
 * coordinates; `behindCamera` is true when the projection wrapped around.
 */
export function placeChip(params: {
  ndc: { x: number; y: number };
  behindCamera: boolean;
  distance: number;
  width: number;
  height: number;
  maxDistance?: number;
}): ChipPlacement {
  const maxDistance = params.maxDistance ?? tuning.granny.presentation.chipMaxDistance;
  const onScreen =
    !params.behindCamera &&
    params.ndc.x >= -1 &&
    params.ndc.x <= 1 &&
    params.ndc.y >= -1 &&
    params.ndc.y <= 1;

  return {
    x: (params.ndc.x * 0.5 + 0.5) * params.width,
    y: (-params.ndc.y * 0.5 + 0.5) * params.height,
    visible: onScreen && params.distance <= maxDistance,
  };
}

export class ActivityChip {
  private readonly root: HTMLElement;
  private readonly icon: HTMLElement;
  private readonly label: HTMLElement;
  private readonly senses: HTMLElement;
  private lastSignature = '';

  constructor(parent: HTMLElement) {
    this.root = document.createElement('div');
    this.root.id = 'activity-chip';
    this.root.innerHTML =
      '<span class="activity-icon"></span>' +
      '<span class="activity-label"></span>' +
      '<span class="activity-senses"></span>';
    parent.appendChild(this.root);

    this.icon = must(this.root, '.activity-icon');
    this.label = must(this.root, '.activity-label');
    this.senses = must(this.root, '.activity-senses');
  }

  hide(): void {
    this.root.classList.remove('visible');
  }

  update(presentation: ActivityPresentation, placement: ChipPlacement): void {
    this.root.classList.toggle('visible', placement.visible);
    if (!placement.visible) return;

    this.root.style.transform = `translate(${placement.x}px, ${placement.y}px) translate(-50%, -100%)`;

    // Rewriting three text nodes every frame at 144Hz is pointless layout churn.
    const signature = `${presentation.icon}|${presentation.label}|${presentation.canSee}|${presentation.hearing}`;
    if (signature === this.lastSignature) return;
    this.lastSignature = signature;

    this.icon.textContent = presentation.icon;
    this.label.textContent = presentation.label;
    this.senses.textContent =
      EYE_ICON[presentation.canSee ? 'open' : 'shut'] + EAR_ICON[presentation.hearing];
    this.root.classList.toggle('blind', !presentation.canSee);
    this.root.classList.toggle('sharp-eared', presentation.hearing === 'sharp');
  }

  dispose(): void {
    this.root.remove();
  }
}

function must(root: HTMLElement, selector: string): HTMLElement {
  const found = root.querySelector<HTMLElement>(selector);
  if (!found) throw new Error(`[cat-game] activity chip is missing ${selector}`);
  return found;
}
